'use client';

/**
 * Rx control center — partner scope (`/rx/:partner`).
 *
 * Same desk as `/rx`, clamped to the partner's merchants / the
 * mailboxes it handles. Superadmins reach it via the URL `:partner`.
 */

import * as React from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { trpc } from '@/lib/api/trpc';
import { RxSupportDesk } from '@/components/rx-support/RxSupportDesk';
import { mergePages, useRxSupportDeskState } from '@/components/rx-support/useRxSupportDeskState';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export default function PartnerRxSupportPage() {
  const { partner: partnerParam } = useParams<{ partner: string }>();
  const partner = partnerParam ?? '';
  const extra = { partner };
  const state = useRxSupportDeskState();
  const utils = trpc.useUtils();
  const api = trpc.rxSupport.partner;

  const counts = api.counts.useQuery(extra, { enabled: !!partner, refetchInterval: 30_000 });
  const mailboxes = api.mailboxes.useQuery(extra, { enabled: !!partner, refetchInterval: 60_000 });
  const list = api.listConversations.useQuery(
    { ...state.listInput, ...extra },
    { enabled: !!partner, refetchInterval: 20_000 },
  );
  const detail = api.getConversation.useQuery(
    { conversationId: state.selectedId ?? '', ...extra },
    { enabled: Boolean(partner && state.selectedId), refetchInterval: 20_000 },
  );

  // Context only: refunds and renewals stay with the TGD Care desk.
  const context = api.context.useQuery(
    { conversationId: state.selectedId ?? '', ...extra },
    { enabled: Boolean(partner && state.selectedId), refetchInterval: 60_000 },
  );

  const invalidate = React.useCallback(async () => {
    await Promise.all([
      utils.rxSupport.partner.listConversations.invalidate(),
      utils.rxSupport.partner.counts.invalidate(),
      utils.rxSupport.partner.mailboxes.invalidate(),
      state.selectedId
        ? utils.rxSupport.partner.getConversation.invalidate({
            conversationId: state.selectedId,
            partner,
          })
        : Promise.resolve(),
    ]);
  }, [utils, state.selectedId, partner]);

  const reply = api.reply.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const note = api.addNote.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const setStatus = api.setStatus.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const assign = api.assign.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(errMsg(e)) });
  const refreshMailbox = api.refreshMailbox.useMutation({
    onSuccess: (m) => {
      toast.success(`${m.address}: send ${m.domainStatus}, receive ${m.receivingStatus}`);
      void invalidate();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const conversations = React.useMemo(() => mergePages(state.pages, list.data?.items), [state.pages, list.data]);

  return (
    <RxSupportDesk
      title="Rx control center"
      subtitle={`Patient support for ${partner || 'this partner'}'s merchants. Replies go out as the brand.`}
      currentUserId={state.currentUserId}
      counts={counts.data}
      mailboxes={mailboxes.data}
      mailboxesLoading={mailboxes.isLoading}
      conversations={conversations}
      conversationsLoading={list.isLoading}
      hasMore={Boolean(list.data?.nextCursor)}
      onLoadMore={() => {
        if (!list.data?.nextCursor) return;
        state.setPages((prev) => [...prev, list.data?.items ?? []]);
        state.setCursor(list.data.nextCursor);
      }}
      filters={state.filters}
      onFiltersChange={state.onFiltersChange}
      selectedId={state.selectedId}
      onSelect={state.setSelectedId}
      detail={detail.data}
      detailLoading={detail.isLoading}
      onReply={async (i) => {
        await reply.mutateAsync({ ...i, partner });
        toast.success('Reply sent');
      }}
      onNote={async (i) => {
        await note.mutateAsync({ ...i, partner });
      }}
      onSetStatus={async (i) => {
        await setStatus.mutateAsync({ ...i, partner });
      }}
      onAssign={async (i) => {
        await assign.mutateAsync({ ...i, partner });
      }}
      onRefreshMailbox={async (mailboxId) => {
        await refreshMailbox.mutateAsync({ mailboxId, partner });
      }}
      context={context.data}
      contextLoading={context.isLoading}
    />
  );
}
